import { useMemo } from "react";
import {
  Control,
  useController,
  useFormContext,
  FieldPathByValue,
  FieldValues,
  RegisterOptions
} from "react-hook-form";
import { DateValueType } from "react-tailwindcss-datepicker";
import DateSelect from "@/components/DateSelect";
import { DateTime } from "luxon";
import { isoToLocalDate } from "../_utils";

interface SingleDatePickerProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  label: string;
  required?: boolean;
  name: FieldPathByValue<TFieldValues, string>;
  supportingText?: string;
  useModal?: boolean;
  minDate?: Date;
  maxDate?: Date;
  rules?: Omit<
    RegisterOptions<TFieldValues, FieldPathByValue<TFieldValues, string>>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
}

export default function SingleDatePicker<TFieldValues extends FieldValues>({
  control,
  label,
  required = false,
  name,
  supportingText,
  useModal = false,
  minDate,
  maxDate,
  rules
}: SingleDatePickerProps<TFieldValues>) {
  const { trigger } = useFormContext<TFieldValues>();
  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController({
    control,
    name,
    rules: {
      validate: (value) => {
        if (!value) {
          return `${label} is required`;
        }

        if (name === "endDate" && minDate) {
          if (DateTime.fromISO(value).toJSDate() < minDate) {
            return "End date must be after start date";
          }
        }

        return true;
      },

      ...rules
    }
  });

  const stableValue = useMemo<DateValueType>(() => ({ startDate: value || null, endDate: value || null }), [value]);
  const stableStartFrom = useMemo(() => isoToLocalDate(value), [value]);

  return (
    <DateSelect
      label={required ? `${label} *` : label}
      startFrom={stableStartFrom}
      value={stableValue}
      showFooter={false}
      onBlur={() => {
        void trigger(name);
      }}
      onChange={(x: DateValueType | string) => {
        if (typeof x === "string") return;
        if (x === null) {
          onChange("");
          return;
        }

        onChange(x.startDate);
      }}
      supportingText={supportingText}
      useSingleDate
      useMultiCalendar={false}
      error={!!error}
      errorMessage={error?.message}
      useModal={useModal}
      minDate={minDate}
      maxDate={maxDate}
    />
  );
}
